<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import Button from "$app/components/ButtonSpinner.svelte"
  import Navigation from "./Navigation.svelte"
  import { ChevronRight, ChevronLeft } from "lucide-svelte"

  import { onboardingStore } from "./onboarding.svelte"
  import { appStore } from "$app/store.svelte"

  import bg from "@images/onboarding/bg-eclipse.jpg"
  import img from "@images/onboarding/one_page_overview.jpg"

  let saving = false

  let errors: any = {}

  async function nextStep(): Promise<void> {
    saving = true
    errors = {}

    try {
      await xhr.patch(`/plan_onboarding/${appStore.plan.id}/complete.json`, {
        plan: { onboarding_step: "next_step" }
      })

      window.location.href = `/plans/${appStore.plan.id}`
    } catch (e) {
      if (e.response.data) {
        errors = e.response.data
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
      toast("There was an error saving your plan data", "error")
    } finally {
      saving = false
    }
  }

  onMount(() => {
    onboardingStore.setBrowserHistory({}, "review")
  })
</script>

<div class="mx-auto px-2 lg:px-6 pt-4 w-full pb-8 lg:pb-20 lg:text-left">
  <div class="px-4 w-full lg:w-1/2 xl:pr-16">
    <Navigation step="review" on:updateStep />

    <h2 class="mt-12 text-5xl font-bold text-left text-white">All Set</h2>

    <p class="mt-8 text-white text-xl max-w-xl">
      You're all done! Go to your plan to continue refining your One Page View
      and complete building out your goal hierarchy. Once your plan is complete
      then start exploring all the other leadership building tools including the
      Activity and Recognition features.
    </p>

    <p class="mt-8 text-white text-xl max-w-xl">
      As you get settled don't hesitate to <a
        href="mailto:support@leadwithpurpose.com"
        class="text-blue-300 hover:underline">contact us</a
      > at any time if you have any questions or want to learn how to best deploy
      Lead With Purpose in your organization. Also, don't forget to check out the
      Lead With Purpose University for our complete set of courses that will get
      you and your team fully trained in the Fast Attack Leadership framework.
    </p>

    <div class="space-y-8 mt-6 md:mt-8 max-w-xl">
      <div>
        <div class="flex items-center justify-between py-4 mt-6">
          <Button
            class="btn btn-rounded btn-big secondary flex items-center gap-1"
            processing={saving}
            on:click={() => {
              onboardingStore.prevStep()
            }}
          >
            <ChevronLeft class="text-gray-500 w-6 h-6" />
            <span class="font-light text-gray-500">PREVIOUS</span>
          </Button>
          <Button
            class="btn btn-rounded success btn-big flex items-center gap-1"
            processing={saving}
            on:click={nextStep}
          >
            <span class="text-white font-extrabold"> Go to my plan </span>
            <ChevronRight class="text-core size-6" />
          </Button>
        </div>
      </div>
    </div>
  </div>
</div>
<div
  class="relative w-full hidden lg:inline lg:absolute lg:inset-y-0 lg:-right-0 md:w-1/3 lg:w-1/2 lg:h-full bg-lwp-light-grey bg-right-bottom bg-no-repeat bg-contain"
  style="background-image: url({bg})"
>
  <img
    src={img}
    alt="One Page View"
    class="absolute shadow-lg rounded inset-y-4 inset-x-6 xl:-inset-x-6 w-full object-fill"
  />
</div>
